<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PaymentRulesEditor from './PaymentRulesEditor.vue';

// The Payment (Pago) tab: declares whether a procedure charges a fee, the base fee (a fixed amount
// plus an optional percentage of a declared intake value, with optional min/max), an optional
// date-of-birth field that enables a computed `age` in rules, and the ordered waiver/discount/
// surcharge rules. The computed fee is snapshotted onto each tramite at creation.
const props = defineProps({
  required: { type: Boolean, default: false },
  currency: { type: String, default: 'MXN' },
  config: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:required', 'update:currency', 'update:config']);

const { t } = useI18n();

const CURRENCIES = ['MXN', 'USD'];

const base = computed(() => props.config.base || {});
const rules = computed(() => props.config.rules || []);

function patchConfig(patch) {
  emit('update:config', { ...props.config, ...patch });
}

function patchBase(patch) {
  patchConfig({ base: { ...base.value, ...patch } });
}

// number inputs emit '' when cleared — normalize to null so the backend treats it as "none".
function num(value) {
  return value === '' || value === null ? null : Number(value);
}
</script>

<template>
  <div class="flex flex-col gap-5 max-w-2xl">
    <label class="flex gap-2 items-center cursor-pointer">
      <input
        type="checkbox"
        class="rounded"
        :checked="required"
        @change="emit('update:required', $event.target.checked)"
      />
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('TRAMITE_TYPES.PAYMENT.REQUIRED') }}
      </span>
    </label>
    <p class="-mt-3 text-xs text-n-slate-10">
      {{ t('TRAMITE_TYPES.PAYMENT.REQUIRED_HELP') }}
    </p>

    <template v-if="required">
      <!-- Currency -->
      <div class="flex flex-col gap-1 w-32">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.PAYMENT.CURRENCY') }}
        </label>
        <select
          class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          :value="currency"
          @change="emit('update:currency', $event.target.value)"
        >
          <option v-for="cur in CURRENCIES" :key="cur" :value="cur">
            {{ cur }}
          </option>
        </select>
      </div>

      <!-- Base fee -->
      <section class="flex flex-col gap-3">
        <div class="flex flex-col gap-0.5">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITE_TYPES.PAYMENT.BASE_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{ t('TRAMITE_TYPES.PAYMENT.BASE_HELP') }}
          </p>
        </div>

        <div class="flex flex-wrap gap-4 items-end">
          <div class="flex flex-col gap-1 w-32">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('TRAMITE_TYPES.PAYMENT.FIXED') }}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              :value="base.fixed ?? ''"
              class="px-2 h-9 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              @input="patchBase({ fixed: num($event.target.value) })"
            />
          </div>
          <span class="pb-2 text-xs text-n-slate-10">+</span>
          <div class="flex flex-col gap-1 w-24">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('TRAMITE_TYPES.PAYMENT.PERCENT') }}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              :value="base.percent ?? ''"
              class="px-2 h-9 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              @input="patchBase({ percent: num($event.target.value) })"
            />
          </div>
          <div class="flex flex-col flex-1 gap-1 min-w-44">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('TRAMITE_TYPES.PAYMENT.PERCENT_OF') }}
            </label>
            <input
              type="text"
              :value="base.percent_of ?? ''"
              :placeholder="t('TRAMITE_TYPES.PAYMENT.PERCENT_OF_PLACEHOLDER')"
              class="px-2 h-9 font-mono text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
              @input="patchBase({ percent_of: $event.target.value })"
            />
          </div>
        </div>

        <div class="flex flex-wrap gap-4">
          <div class="flex flex-col gap-1 w-32">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('TRAMITE_TYPES.PAYMENT.MIN') }}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              :value="base.min ?? ''"
              class="px-2 h-9 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              @input="patchBase({ min: num($event.target.value) })"
            />
          </div>
          <div class="flex flex-col gap-1 w-32">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('TRAMITE_TYPES.PAYMENT.MAX') }}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              :value="base.max ?? ''"
              class="px-2 h-9 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              @input="patchBase({ max: num($event.target.value) })"
            />
          </div>
        </div>
      </section>

      <!-- Date-of-birth field (enables computed age) -->
      <div class="flex flex-col gap-1 max-w-sm">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TRAMITE_TYPES.PAYMENT.BIRTHDATE_ATTR') }}
        </label>
        <input
          type="text"
          :value="config.birthdate_attribute ?? ''"
          :placeholder="t('TRAMITE_TYPES.PAYMENT.BIRTHDATE_PLACEHOLDER')"
          class="px-3 py-2 w-full font-mono text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
          @input="patchConfig({ birthdate_attribute: $event.target.value })"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('TRAMITE_TYPES.PAYMENT.BIRTHDATE_HELP') }}
        </span>
      </div>

      <!-- Rules -->
      <section class="flex flex-col gap-3 pt-2 border-t border-n-weak">
        <div class="flex flex-col gap-0.5">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TRAMITE_TYPES.PAYMENT.RULES.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{ t('TRAMITE_TYPES.PAYMENT.RULES.HELP') }}
          </p>
        </div>
        <PaymentRulesEditor
          :model-value="rules"
          @update:model-value="patchConfig({ rules: $event })"
        />
      </section>
    </template>
  </div>
</template>
